/**
 * Member Profile Page
 * 
 * Shows member profile information
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import apiClient, { getUploadUrl } from '@/utils/api';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  Users, 
  Edit,
  Calendar,
  Shield
} from 'lucide-react';

const MemberProfile = () => {
  const [profile, setProfile] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, notifsRes] = await Promise.all([
        apiClient.get('/profile/me'),
        apiClient.get('/notifications/my')
      ]);
      setProfile(profileRes.data);
      setNotifications(notifsRes.data || []);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    return getUploadUrl(url);
  };

  const unreadNotifications = notifications.filter(n => !n.is_read);

  if (loading) {
    return (
      <MemberLayout currentPage="profile" title="My Profile" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  if (!profile) {
    return (
      <MemberLayout currentPage="profile" title="My Profile" notificationCount={unreadNotifications.length}>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Failed to load profile</p>
          </CardContent>
        </Card>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="profile" title="My Profile" notificationCount={unreadNotifications.length}>
      {/* Profile Header */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {profile.portrait_photo_url ? (
                <img 
                  src={getPhotoUrl(profile.portrait_photo_url)} 
                  alt="Profile" 
                  className="w-16 h-16 rounded-full object-cover border-2 border-primary"
                  data-testid="profile-photo"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.style.display = 'none';
                    if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              {profile.portrait_photo_url ? (
                <div className="w-16 h-16 rounded-full bg-primary/10 items-center justify-center text-lg font-semibold text-primary" style={{ display: 'none' }} data-testid="profile-initials-fallback">
                  {`${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() || <User className="w-8 h-8 text-primary" />}
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-lg font-semibold text-primary" data-testid="profile-initials-avatar">
                  {`${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase() || <User className="w-8 h-8 text-primary" />}
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold">
                  {profile.title} {profile.first_name} {profile.last_name}
                </h2>
                <p className="text-muted-foreground">{profile.email}</p>
                <Badge variant="outline" className="mt-1 bg-green-50 text-green-700 border-green-200">
                  {profile.status?.charAt(0).toUpperCase() + profile.status?.slice(1) || 'Active'}
                </Badge>
              </div>
            </div>
            <Link to="/dashboard/profile/edit">
              <Button data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Title</p>
                <p className="font-medium">{profile.title || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Username</p>
                <p className="font-medium">{profile.username || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">First Name</p>
                <p className="font-medium">{profile.first_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Name</p>
                <p className="font-medium">{profile.last_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gender</p>
                <p className="font-medium">{profile.gender || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date of Birth</p>
                <p className="font-medium">{profile.date_of_birth || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">NRC/Passport</p>
                <p className="font-medium">{profile.nrc_passport || 'Not set'}</p>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{profile.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Contact Number</p>
                  <p className="font-medium">{profile.contact_number || 'Not set'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Residential Address</p>
                  <p className="font-medium">{profile.residential_address || 'Not set'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Professional Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Work Station</p>
                <p className="font-medium">{profile.work_station || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Specialty</p>
                <p className="font-medium">{profile.specialty || 'Not set'}</p>
              </div>
            </div>

            <div className="pt-4 border-t space-y-3">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Member ID</p>
                  <p className="font-medium font-mono text-sm">{profile.member_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium">
                    {profile.created_at 
                      ? new Date(profile.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Member Status</p>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {profile.status?.charAt(0).toUpperCase() + profile.status?.slice(1) || 'Active'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Next of Kin */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Next of Kin
          </CardTitle>
          <CardDescription>Emergency contact information</CardDescription>
        </CardHeader>
        <CardContent>
          {profile.next_of_kin ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium">
                  {profile.next_of_kin.full_name || 
                   (profile.next_of_kin.first_name || profile.next_of_kin.surname 
                    ? `${profile.next_of_kin.first_name || ''} ${profile.next_of_kin.surname || ''}`.trim() 
                    : 'Not set')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gender</p>
                <p className="font-medium">
                  {profile.next_of_kin.gender 
                    ? (profile.next_of_kin.gender === 'Male' ? 'M' : profile.next_of_kin.gender === 'Female' ? 'F' : profile.next_of_kin.gender)
                    : 'Not set'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Relationship</p>
                <p className="font-medium">{profile.next_of_kin.relationship || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Contact Number</p>
                <p className="font-medium">{profile.next_of_kin.contact_number || 'Not set'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Address</p>
                <p className="font-medium">{profile.next_of_kin.address || profile.next_of_kin.residential_address || 'Not set'}</p>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">No next of kin information provided</p>
          )}
        </CardContent>
      </Card>

      {/* Beneficiaries */}
      {profile.beneficiaries && profile.beneficiaries.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Beneficiaries
            </CardTitle>
            <CardDescription>{profile.beneficiaries.length} beneficiary(ies)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {profile.beneficiaries.map((ben, index) => (
                <div key={index} className="p-4 border rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Full Name</p>
                      <p className="font-medium">{ben.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Relationship</p>
                      <p className="font-medium">{ben.relationship}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Date of Birth</p>
                      <p className="font-medium">
                        {ben.date_of_birth 
                          ? new Date(ben.date_of_birth).toLocaleDateString()
                          : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Age</p>
                      <p className="font-medium">
                        {ben.date_of_birth 
                          ? Math.floor((new Date() - new Date(ben.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))
                          : 'Not set'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card className="mt-6">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/dashboard/profile/edit" className="flex-1">
              <Button variant="outline" className="w-full" data-testid="edit-profile-btn-2">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            </Link>
            <Link to="/dashboard/profile/edit?tab=password" className="flex-1">
              <Button variant="outline" className="w-full" data-testid="change-password-btn">
                <Shield className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </MemberLayout>
  );
};

export default MemberProfile;
